import { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Chip,
  Card,
  CardContent,
  Badge,
  Stack,
  Paper,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import HeadphonesIcon from "@mui/icons-material/Headphones";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
 
const theme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: "#E8650A" },
    background: { default: "#2C1A0E", paper: "#3D2210" },
  },
  typography: {
    fontFamily: "'Tiro Devanagari Hindi', 'Noto Sans Devanagari', serif",
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: "#3D2210",
          border: "1px solid rgba(232,101,10,0.15)",
          borderRadius: 16,
          transition: "transform 0.25s ease, box-shadow 0.25s ease",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 12px 32px rgba(232,101,10,0.2)",
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          border: "1px solid rgba(232,101,10,0.4)",
          color: "#E8650A",
          backgroundColor: "transparent",
          fontSize: "0.72rem",
          height: 26,
        },
      },
    },
  },
});
 
const categories = [
  {
    id: 1,
    count: "500+",
    title: "संगीत पूजा",
    subtitle: "Musical Worship",
    langs: ["हिंदी", "मराठी", "कन्नड"],
    features: ["संपूर्ण अर्थ", "शुद्ध उच्चार", "कराओके उपलब्ध"],
    accent: "#E8650A",
  },
  {
    id: 2,
    count: "300+",
    title: "अभिषेक पाठ",
    subtitle: "Abhishek Path",
    langs: ["हिंदी", "मराठी", "कन्नड"],
    features: ["विधी माहिती", "मंत्र उच्चार", "ऑडिओ गाईड"],
    accent: "#E8650A",
  },
  {
    id: 3,
    count: "400+",
    title: "अष्टक व स्तोत्र",
    subtitle: "Ashtak & Stotra",
    langs: ["हिंदी", "मराठी", "कन्नड"],
    features: ["अर्थासह", "शास्त्रशुद्ध", "व्हिडिओ उपलब्ध"],
    accent: "#E8650A",
  },
  {
    id: 4,
    count: "250+",
    title: "अर्घ्य व जयमाला",
    subtitle: "Arghya & Jaymala",
    langs: ["हिंदी", "मराठी", "कन्नड"],
    features: ["पारंपरिक", "भावपूर्ण", "HD ऑडिओ"],
    accent: "#E8650A",
  },
  {
    id: 5,
    count: "200+",
    title: "सवाल (बोली)",
    subtitle: "Sawal (Boli)",
    langs: ["हिंदी", "मराठी", "कन्नड"],
    features: ["पूजेमधील", "चढावे", "स्पष्ट उच्चार"],
    accent: "#C0392B",
  },
  {
    id: 6,
    count: "150+",
    title: "शांती मंत्र",
    subtitle: "Shanti Mantra",
    langs: ["हिंदी", "मराठी", "कन्नड"],
    features: ["ध्यान", "मंत्रोच्चार", "शास्त्रीय"],
    accent: "#E8650A",
  },
];
 
function MusicIconBox({ accent }) {
  return (
    <Box
      sx={{
        width: 52,
        height: 52,
        borderRadius: "14px",
        background: `linear-gradient(135deg, ${accent} 0%, ${accent}cc 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: `0 4px 16px ${accent}55`,
        mb: 1.5,
      }}
    >
      <MusicNoteIcon sx={{ color: "#fff", fontSize: 26 }} />
    </Box>
  );
}
 
function CategoryCard({ cat }) {
  return (
    <Card sx={{ height: "100%", position: "relative" }}>
      <CardContent sx={{ p: 2.5 }}>
        {/* Count badge */}
        <Box sx={{ position: "absolute", top: 14, right: 14 }}>
          <Box
            sx={{
              background: "#E8650A",
              color: "#fff",
              borderRadius: "10px",
              px: 1.2,
              py: 0.2,
              fontSize: "0.75rem",
              fontWeight: 700,
            }}
          >
            {cat.count}
          </Box>
        </Box>
 
        <MusicIconBox accent={cat.accent} />
 
        <Typography
          variant="h6"
          sx={{
            fontWeight: 800,
            color: "#fff",
            fontSize: "1.05rem",
            lineHeight: 1.3,
            mb: 0.4,
          }}
        >
          {cat.title}
        </Typography>
        <Typography
          sx={{
            color: "#E8650A",
            fontStyle: "italic",
            fontSize: "0.82rem",
            mb: 1.5,
          }}
        >
          {cat.subtitle}
        </Typography>
 
        {/* Language chips */}
        <Stack direction="row" spacing={0.7} flexWrap="wrap" mb={2}>
          {cat.langs.map((l) => (
            <Chip key={l} label={l} size="small" />
          ))}
        </Stack>
 
        {/* Features */}
        <Stack spacing={0.6}>
          {cat.features.map((f) => (
            <Box key={f} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FiberManualRecordIcon
                sx={{ color: "#E8650A", fontSize: 8, flexShrink: 0 }}
              />
              <Typography sx={{ color: "rgba(255,255,255,0.8)", fontSize: "0.82rem" }}>
                {f}
              </Typography>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}
 
export default function JainSangeetLibrary() {
  return (
    <ThemeProvider theme={theme}>
      {/* Google Fonts */}
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Tiro+Devanagari+Hindi:ital@0;1&family=Noto+Sans+Devanagari:wght@400;700;900&display=swap%27);`}</style>
 
      <Box
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(160deg, #2C1A0E 0%, #1E1005 100%)",
          py: 6,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative background music note */}
        <MusicNoteIcon
          sx={{
            position: "absolute",
            left: 32,
            top: 80,
            fontSize: 120,
            color: "rgba(232,101,10,0.07)",
            transform: "rotate(-15deg)",
          }}
        />
        <MusicNoteIcon
          sx={{
            position: "absolute",
            right: 32,
            bottom: 100,
            fontSize: 100,
            color: "rgba(232,101,10,0.05)",
            transform: "rotate(12deg)",
          }}
        />
 
        <Container maxWidth="lg">
          {/* Header pill */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 2.5,
                py: 1,
                borderRadius: 30,
                background: "linear-gradient(135deg, #E8650A, #C0392B)",
                boxShadow: "0 4px 20px rgba(232,101,10,0.35)",
              }}
            >
              <GraphicEqIcon sx={{ color: "#fff", fontSize: 18 }} />
              <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.9rem", letterSpacing: 1.5 }}>
                संगीत संग्रह
              </Typography>
            </Box>
          </Box>
 
          {/* Main title */}
          <Typography
            variant="h2"
            align="center"
            sx={{
              fontWeight: 900,
              color: "#fff",
              fontSize: { xs: "2rem", md: "3rem" },
              lineHeight: 1.2,
              mb: 1.5,
              textShadow: "0 2px 24px rgba(232,101,10,0.2)",
            }}
          >
            जैन संगीत व कराओके लायब्ररी
          </Typography>
 
          {/* Orange underline accent */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2.5 }}>
            <Box sx={{ width: 80, height: 3, background: "#E8650A", borderRadius: 2 }} />
          </Box>
 
          {/* Subtitle */}
          <Typography
            align="center"
            sx={{
              color: "rgba(255,255,255,0.65)",
              fontSize: "1rem",
              mb: 6,
              maxWidth: 600,
              mx: "auto",
              lineHeight: 1.8,
            }}
          >
            हिंदी, मराठी आणि कन्नड भाषांमध्ये संपूर्ण अर्थ, विधी माहिती व शुद्ध उच्चारणासह
          </Typography>
 
          {/* Cards grid */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(3, 1fr)",
              },
              gap: 3,
              mb: 5,
            }}
          >
            {categories.map((cat) => (
              <CategoryCard key={cat.id} cat={cat} />
            ))}
          </Box>
 
          {/* Bottom karaoke banner */}
          <Paper
            elevation={0}
            sx={{
              background: "linear-gradient(135deg, #7B3B0C 0%, #5A2A08 100%)",
              borderRadius: 4,
              p: 4,
              textAlign: "center",
              border: "1px solid rgba(232,101,10,0.25)",
            }}
          >
            <HeadphonesIcon sx={{ color: "#E8650A", fontSize: 44, mb: 1.5 }} />
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, color: "#fff", mb: 1, fontSize: "1.1rem" }}
            >
              कराओके (बिनवाणी) व गाण्याच्या ट्रॅकसह उपलब्ध
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "0.9rem", maxWidth: 560, mx: "auto" }}>
              हे विशेषतः पंडित वर्ग, पूजा विधी करणारे श्रावक-श्राविका व संघांसाठी अत्यंत उपयुक्त ठरेल
            </Typography>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
 